import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SETS, ETSY_SHOP } from "../data/site";
import { PRODUCTS } from "../data/catalog";
import { useEtsyListings, withEtsy } from "../data/etsy";
import { ProductCard } from "./ProductCard";
import { Reveal } from "./motion";

export default function SetPage() {
  const { slug: setSlug } = useParams();
  const etsyListings = useEtsyListings();
  const set = SETS.find((s) => s.id === setSlug);
  if (!set) return <Navigate to="/" replace />;

  const sourceItems = set.fromCollection
    ? PRODUCTS.filter((p) => p.collection === set.fromCollection).map((p) => ({ name: p.name }))
    : set.items;

  const items = sourceItems.map((item) => {
    const catalogMatch = PRODUCTS.find((p) => p.name === item.name);
    const merged = catalogMatch
      ? { ...item, phrase: item.phrase || catalogMatch.tagline, personalization: item.personalization || catalogMatch.personalization }
      : { ...item, phrase: item.phrase || item.detail };
    return withEtsy(merged, etsyListings);
  });

  return (
    <main data-testid={`set-page-${set.id}`}>
      <div className={`${set.bg} border-b ${set.inverse ? "border-cream/15" : "border-navy/10"}`}>
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-20">
          <Reveal>
            <Link
              to="/"
              data-testid="set-back-link"
              className={`group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
                set.inverse ? "text-cream/60 hover:text-cream" : "text-navy/60 hover:text-navy"
              }`}
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              All types
            </Link>
            <h1
              className={`mt-10 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight md:text-7xl lg:text-8xl ${
                set.inverse ? "text-cream" : "text-navy"
              }`}
            >
              {set.title}
            </h1>
            <p
              className={`mt-8 max-w-xl font-playfair text-xl italic leading-relaxed md:text-2xl ${
                set.inverse ? "text-cream/70" : "text-navy/70"
              }`}
            >
              “{set.line}”
            </p>
            <p
              className={`mt-8 text-[11px] font-medium uppercase tracking-[0.3em] ${
                set.inverse ? "text-cream/50" : "text-navy/50"
              }`}
            >
              Each piece sold individually on Etsy — mix and match your own set
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal key={item.name} delay={Math.min((i % 4) * 0.06, 0.24)} className="h-full">
              <ProductCard item={item} setId={set.id} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <div className="flex flex-wrap items-center justify-between gap-4 border border-navy/25 bg-blush/50 px-8 py-7 md:px-10">
            <p className="max-w-md text-sm leading-relaxed text-navy/70">
              Every style on this page is its own Etsy listing — buy one, or build the full look piece by piece.
            </p>
            <a
              href={ETSY_SHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="set-follow-etsy-btn"
              className="group flex items-center gap-3 rounded-full bg-navy px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
            >
              Follow the shop on Etsy
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
